import { useCallback, useSyncExternalStore } from 'react';

/**
 * A reader preference kept in localStorage, shared by every caller: the
 * Settings control that changes it and whatever part of the app follows it
 * re-render together (`useSyncExternalStore`). The value lives in memory
 * and is written through, so a failed write (private mode) still applies
 * for the session. `apply` runs on every change, and once from
 * `applyStoredPreferences()` at startup -- for preferences that land on
 * `<html>` as an attribute or CSS variable.
 *
 * `useFont`, `useLineHeight` and `useMotionPreference` predate this and
 * hand-roll the same shape.
 */
export interface PreferenceStore<T> {
	use: () => [T, (next: T) => void];
	get: () => T;
	set: (next: T) => void;
	apply: () => void;
}

const stores: Array<PreferenceStore<unknown>> = [];

export function createPreferenceStore<T extends string | number | boolean>({
	key,
	fallback,
	values,
	apply,
	external = false,
}: {
	key: string;
	fallback: T;
	/** The only values accepted from storage; anything else reads as `fallback`. */
	values: ReadonlyArray<T>;
	apply?: (value: T) => void;
	/**
	 * Another part of the app also writes this key directly (the reader's
	 * own mode switches), so read storage every time instead of trusting
	 * the in-memory copy.
	 */
	external?: boolean;
}): PreferenceStore<T> {
	const listeners = new Set<() => void>();
	let current: T | undefined;

	const read = (): T => {
		try {
			const stored = localStorage.getItem(key);
			const match = values.find((value) => String(value) === stored);
			return match ?? current ?? fallback;
		} catch {
			return current ?? fallback;
		}
	};

	const get = () => {
		if (external) {
			return read();
		}
		current ??= read();
		return current;
	};

	const set = (next: T) => {
		current = next;
		try {
			localStorage.setItem(key, String(next));
		} catch {
			// Private mode etc.: still applies for this session.
		}
		apply?.(next);
		listeners.forEach((listener) => listener());
	};

	const subscribe = (listener: () => void) => {
		listeners.add(listener);
		return () => listeners.delete(listener);
	};

	const store: PreferenceStore<T> = {
		get,
		set,
		apply: () => apply?.(get()),
		use: function usePreference() {
			const value = useSyncExternalStore(subscribe, get, () => fallback);
			const setValue = useCallback((next: T) => set(next), []);
			return [value, setValue];
		},
	};
	stores.push(store as PreferenceStore<unknown>);
	return store;
}

/** Applies every store's saved value; called once from `main.tsx`. */
export function applyStoredPreferences() {
	for (const store of stores) {
		store.apply();
	}
}

/** Sets or removes an attribute on `<html>`; `null` removes it. */
export function setRootAttribute(name: string, value: string | null) {
	if (value === null) {
		document.documentElement.removeAttribute(name);
	} else {
		document.documentElement.setAttribute(name, value);
	}
}
